import React, { Fragment, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import {
  fetchVehicleWeights,
  fetchVehicleBatteryLogs,
  fetchVehicleTemperatureLogs,
  fetchVehicleHumidityLogs,
  downloadClimateLogs,
  climateSensorReportTypeOptions,
  fetchLpsBatteryLogs
} from '../../../../../state/actions/vehicles'
import { useAuth0 } from '@auth0/auth0-react'
import LogChart from '@modul-connect/shared/components/organisms/logChart'
import moment from 'moment'
import { DateTime } from 'luxon'
import KpiRow from '@modul-connect/shared/components/molecules/kpiRow/kpiRow'
import { ticks_24_hours, ticks_7_days, ticks_custom } from './ticks'
import theme from '@modul-connect/shared/theme'
import { convertToSelectedUnit } from '../../../../../utils/unitConverter'
import { BubbleLoadingWidget } from '@modul-connect/shared/components/molecules/bubbleLoadingWidget/bubbleLoadingWIdget'
import { P } from '@modul-connect/shared/components/atoms/text'
import BatteryLogGraph from './batteryLogGraph'
import { getHumidityWarningLevel, getTemperatureWarningLevel, getHumiditySensors, getThermometers, getWeightWarningColor } from '../../../../../utils/vehicleUtils'
import { getServiceList } from '../../../../../utils/ServiceList'
import Paper from '@modul-connect/shared/components/atoms/paper'
import { ClimateSensorGraph } from './climateSensorGraph'
import { isHumiditySensorBroken } from '@modul-connect/shared/config'
import { ServiceType } from '@modul-connect/shared/utils/services'
import LpsLogGraph from './lpsLogGraph'
import { useTranslation } from 'react-i18next'


export const getDaysDifference = (startTime, endTime) => {
  const diffTime = Math.abs(endTime - startTime)
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

const groupByBatteryId = (logs) => {
  const grouped = {}

  logs.forEach(log => {
    const id = log.id
    if (!grouped[id]) {
      grouped[id] = []
    }
    grouped[id].push(log)
  })

  return grouped
}

export const intervals = {
  LAST_24_H: '24 hours',
  LAST_WEEK: '7 days',
}

export const getTicks = (interval, isCustom = false, startTime, endTime) => {
  if(isCustom)
    return ticks_custom(startTime, endTime)
  else
    switch (interval) {
      case intervals.LAST_24_H:
        return ticks_24_hours()
      case intervals.LAST_WEEK:
        return ticks_7_days()
      default:
        return []
    }
}

const LogGraphs = ({
  vehicleId,
  vehicle,
  battery_logs,
  weight_logs,
  temperature_logs,
  humidity_logs,
  lps_logs,
  fetchVehicleWeights,
  fetchVehicleBatteryLogs,
  fetchVehicleTemperatureLogs,
  fetchVehicleHumidityLogs,
  fetchLpsBatteryLogs,
  log_loading,
  vehicle_loading,
  loading,
  subtrees,
  downloadClimateLogs,
  downloading
}) => {
  const { getAccessTokenSilently } = useAuth0()
  const {t} = useTranslation()
  

  const getSamplingInterval = (interval) => {
    switch (interval) {
      case intervals.LAST_24_H:
        return 10
      case intervals.LAST_WEEK:
        return 60
      default:
        return 0
    }
  }

  const getStartTS = (interval) => {
    switch (interval) {
      case intervals.LAST_24_H:
        return DateTime.local().minus({ days: 1 }).startOf('hour')
      case intervals.LAST_WEEK:
        return DateTime.local().minus({ weeks: 1 }).startOf('day')
      default:
        return DateTime.local().minus({ days: 1 }).startOf('hour')
    }
  }

  const getEndTs = () => {
    return DateTime.local()
  }


  

  const getSamplingIntervalBasedOnDates = (startTime, endTime) => {
    const diffDays = getDaysDifference(startTime, endTime)
    if(diffDays <= 1)
      return 10
    else if(diffDays <= 7)
      return 60
    else 
      return 120
  }


  const [batteryStartTime, setBatteryStartTime] = React.useState(DateTime.now().startOf('day'))
  const [batteryEndTime, setBatteryEndTime] = React.useState(DateTime.now())

  const [weightStartTime, setWeightStartTime] = React.useState(DateTime.now().startOf('day'))
  const [weightEndTime, setWeightEndTime] = React.useState(DateTime.now())

  const [lpsStartTime, setLpsStartTime] = React.useState(DateTime.now().startOf('day'))
  const [lpsEndTime, setLpsEndTime] = React.useState(DateTime.now())

  const [temperatureStartTime, setTemperatureStartTime] = React.useState(DateTime.now().startOf('day'))
  const [temperatureEndTime, setTemperatureEndTime] = React.useState(DateTime.now())

  const [humidtyStartTime, setHumidtyStartTime] = React.useState(DateTime.now().startOf('day'))
  const [humidtyEndTime, setHumidtyEndTime] = React.useState(DateTime.now())

  const filterWeights = () => {
    const relevant = weight_logs[vehicleId]
    if (!relevant) return null
    const samplingInterval = getSamplingIntervalBasedOnDates(weightStartTime, weightEndTime)
    return relevant.filter(log => {
      if (log.status === 255 || log.samplingInterval !== samplingInterval) return false
      const timestamp = DateTime.fromISO(log.timestamp)
      return timestamp <= weightEndTime && timestamp >= weightStartTime
    })
  }

  const fetchVehicleWeightLogsWithAccessToken = () => {
    getAccessTokenSilently()
      .then(accessToken => {
        fetchVehicleWeights(
          accessToken,
          weightStartTime,
          weightEndTime,
          vehicleId,
          getSamplingIntervalBasedOnDates(weightStartTime, weightEndTime))
      })
  }

  const fetchVehicleBatteryLogsWithAccessToken = () => {
    getAccessTokenSilently()
      .then(accessToken => {
        fetchVehicleBatteryLogs(
          accessToken,
          batteryStartTime,
          batteryEndTime,
          vehicleId,
          getSamplingIntervalBasedOnDates(batteryStartTime, batteryEndTime))
      })
  }

  const fetchVehicleLpsLogsWithAccessToken = () => {
    getAccessTokenSilently()
      .then(accessToken => {
        fetchLpsBatteryLogs(
          accessToken,
          lpsStartTime,
          lpsEndTime,
          vehicleId,
          getSamplingIntervalBasedOnDates(lpsStartTime, lpsEndTime))
      })
  }
          
  const fetchVehicleTemperatureLogsWithAcessToken = () => {
    getAccessTokenSilently()
      .then(accessToken => {
        fetchVehicleTemperatureLogs(
          accessToken,
          temperatureStartTime,
          temperatureEndTime,
          vehicleId,
          getSamplingIntervalBasedOnDates(temperatureStartTime, temperatureEndTime))
      })
  }

  const fetchVehicleHumidityLogsWithAcessToken = () => {
    getAccessTokenSilently()
      .then(accessToken => {
        fetchVehicleHumidityLogs(
          accessToken,
          humidtyStartTime,
          humidtyEndTime,
          vehicleId,
          getSamplingIntervalBasedOnDates(humidtyStartTime, humidtyEndTime))
      })
  }

  useEffect(() =>{
    fetchVehicleBatteryLogsWithAccessToken()
  },[batteryStartTime, batteryEndTime])

  useEffect(() =>{
    fetchVehicleWeightLogsWithAccessToken()
  },[weightStartTime, weightEndTime])

  useEffect(() =>{
    fetchVehicleLpsLogsWithAccessToken()
  },[lpsStartTime, lpsEndTime])

  const onTemperatureIntervalChanged = (newInterval) => {
    setTemperatureLogInterval(newInterval)
    fetchVehicleTemperatureLogsWithAcessToken(newInterval)
  }

  useEffect(() =>{
    fetchVehicleTemperatureLogsWithAcessToken()
  },[temperatureStartTime, temperatureEndTime])


  useEffect(() =>{
    fetchVehicleHumidityLogsWithAcessToken()
  },[humidtyStartTime, humidtyEndTime])

  const filtered_weights = filterWeights()
  const prepared_weights = filtered_weights ? filtered_weights.map(weight => {
    return {
      ...weight,
      timestamp: moment(weight.timestamp).unix(),
      max: vehicle?.vehicle?.maxWeight?.maxGross
    }
  }) : []
  prepared_weights.forEach(weight => {
    weight.gross = weight.gross && Number.parseInt(convertToSelectedUnit(weight.gross, 0, 'weight').replace(' ', '').replace(',', ''))
    weight.front = weight.front && Number.parseInt(convertToSelectedUnit(weight.front, 0, 'weight').replace(' ', '').replace(',', ''))
    weight.rear = weight.rear && Number.parseInt(convertToSelectedUnit(weight.rear, 0, 'weight').replace(' ', '').replace(',', ''))
    weight.max = weight.max && Number.parseInt(convertToSelectedUnit(weight.max, 0, 'weight').replace(' ', '').replace(',', ''))
  })

  const prepared_battery_logs = battery_logs ? battery_logs.filter(b => b.boxId === vehicleId).map(log => {
    return {
      ...log,
      timestamp: moment(log.timestamp).unix()
    }
  }) : []

  const prepared_temperature_logs = temperature_logs ? temperature_logs.map(array =>
    array.map(log => {
      return {
        boxId: log.boxId,
        id: log.id,
        telematicsId: log.telematicsId,
        celcius: log.celcius.toFixed(1),
        max: log.highThreshold,
        min: log.lowThreshold,
        warning_max: (log.highThreshold === null || log.highThreshold === undefined) ? undefined : log.highThreshold - 2,
        warning_min: (log.lowThreshold === null || log.lowThreshold === undefined) ? undefined : log.lowThreshold + 2,
        timestamp: moment(log.timestamp).unix(),
      }
    })) : []

  const prepared_humidity_logs = humidity_logs ? humidity_logs.map(array => array.map(log => {
    return {
      boxId: log.boxId,
      id: log.id,
      telematicsId: log.telematicsId,
      humidity: log.humidity.toFixed(0),
      max: log.highThreshold,
      min: log.lowThreshold,
      timestamp: moment(log.timestamp).unix(),
    }
  })) : []

  const prepared_lps_logs = lps_logs ? lps_logs.map(log => {
    return {
      ...log,
      timestamp: moment(log.timestamp).unix(),
    }
  }) : []

  const logs_per_battery = groupByBatteryId(prepared_battery_logs)

  const getPercentage = (maxValue, value) => {
    if (!value || !maxValue)
      return null

    return ((value / maxValue) * 100).toFixed(0)
  }

  // newly fetched values may be more up to date than what we get from currentStatus (VEHICLE_STATUS_UPDATED does not update components)
  const fetchedGross = filtered_weights?.length ? filtered_weights[filtered_weights.length - 1].gross : undefined
  const fetchedAxle1 = filtered_weights?.length ? filtered_weights[filtered_weights.length - 1].axle1 : undefined
  const fetchedAxle2 = filtered_weights?.length ? filtered_weights[filtered_weights.length - 1].axle2 : undefined
  const fetchedNet = filtered_weights?.length ? filtered_weights[filtered_weights.length - 1].net : undefined

  const gross_percentage = getPercentage(vehicle?.vehicle?.maxWeight?.maxGross, fetchedGross ?? vehicle?.currentStatus?.weightSystem?.gross)
  const front_percentage = getPercentage(vehicle?.vehicle?.maxWeight?.maxFront, fetchedAxle1 ?? vehicle?.currentStatus?.weightSystem?.axle1)
  const rear_percentage = getPercentage(vehicle?.vehicle?.maxWeight?.maxRear, fetchedAxle2 ?? vehicle?.currentStatus?.weightSystem?.axle2)

  const gross_value = vehicle.currentStatus?.weightSystem?.gross || filtered_weights?.length ?
    convertToSelectedUnit(fetchedGross ?? vehicle.currentStatus?.weightSystem?.gross, 0, 'weight')
    : undefined
  const capacity_value = vehicle.vehicle.maxWeight?.maxGross && (fetchedGross || vehicle.currentStatus?.weightSystem?.gross) ?
    convertToSelectedUnit(vehicle.vehicle.maxWeight.maxGross - (fetchedGross ?? vehicle.currentStatus?.weightSystem.gross), 0, 'weight') : undefined

  const splitGross = gross_value?.replace(',', ' ').split(' ')
  const weightUnit = splitGross?.pop()
  const grossValue = splitGross?.join(' ')
  const splitCapacity = capacity_value?.replace(',', ' ').split(' ')
  if (splitCapacity) splitCapacity.pop()
  const capacityValue = splitCapacity?.join(' ')

  const kpiData_weight = [
    {
      title:  t('vehicleDetails.currentWeight'),
      value: grossValue ?? '-',
      unit: weightUnit,
      color: getWeightWarningColor(gross_percentage),
      width: 200,
    },
    {
      title: t('vehicleDetails.capacityLeft'),
      value: capacityValue ?? '-',
      unit: weightUnit,
      color: getWeightWarningColor(gross_percentage),
      width: 200,
    },
    {
      title: t('vehicleDetails.totalLoad'),
      value: gross_percentage !== null ? gross_percentage : '-',
      unit: '%',
      color: getWeightWarningColor(gross_percentage),
      width: 200,
    },
    {
      title: t('vehicleDetails.frontLoad'),
      value: front_percentage !== null ? front_percentage : '-',
      unit: '%',
      color: getWeightWarningColor(front_percentage),
      width: 200,
    },
    {
      title:t('vehicleDetails.rearLoad'),
      value: rear_percentage !== null ? rear_percentage : '-',
      unit: '%',
      color: getWeightWarningColor(rear_percentage),
      width: 200,
    }
  ]
  const kpiData_temperature = []

  const rawMax = Math.max(
    vehicle?.vehicle?.maxWeight?.maxGross + 400 || 0,
    Math.ceil(Math.max(...prepared_weights.map(log => log.gross)) / 10) * 10 || 0
  )

  const min = ((vehicle.currentStatus?.weightSystem?.gross || fetchedGross) && (vehicle.currentStatus?.weightSystem?.net || fetchedNet)) ?
    (fetchedGross ?? vehicle.currentStatus?.weightSystem?.gross) - (fetchedNet ?? vehicle.currentStatus?.weightSystem?.net) - 50 :
    Math.min(
      (Math.floor(Math.min(...prepared_weights.map(log => log.gross)) / 100) * 100) - 100,
      vehicle?.vehicle?.maxWeight?.maxGross - 100
    )

  const adjustedMax = Math.ceil(rawMax) + 50

  // Note: Anders XC60 has two thermometers, because one was installed temporarily. All other thermometers have id == 0.
  // This may need to be changed in the future.
  getThermometers(vehicle).forEach(thermometer => {
    const warningLevel = getTemperatureWarningLevel(vehicle, thermometer?.celcius.toFixed(1), thermometer.id)
    kpiData_temperature.push(
      {
      //thermometer.id
        title: `${t('vehicleDetails.temperature')}` + ' — ' + `${vehicle.climateSensors?.find(sensor => sensor.sensor_id == thermometer.id).name
          ? vehicle.climateSensors?.find(sensor => sensor.sensor_id == thermometer.id).name
          : thermometer.id}`,
        value: thermometer?.celcius.toFixed(1),
        unit: '°C',
        color: theme.colors.tag[warningLevel ?? 'primary'],
        width: 230
      }
    )})


  const kpiData_humidity = []
  getHumiditySensors(vehicle).forEach(humiditySensor => {
    const warningLevel = getHumidityWarningLevel(vehicle, humiditySensor?.humidity.toFixed(0), humiditySensor.id)
    kpiData_humidity.push(
      {
        title:
        `${t('vehicleDetails.humidity')} — ` +
        (
          vehicle?.climateSensors?.find(
            sensor => sensor.sensor_id == humiditySensor.id
          )?.name
            ? vehicle.climateSensors.find(
                sensor => sensor.sensor_id == humiditySensor.id
              ).name
            : humiditySensor.id
        ),
        value: humiditySensor?.humidity.toFixed(0),
        unit: '%',
        color: theme.colors.tag[warningLevel ?? 'primary']
      }
    )})
  
  const services = getServiceList({ subtrees })

  const downloadClimateLogsWithAccessToken = (type) => {
    getAccessTokenSilently()
      .then(accessToken => downloadClimateLogs(
        accessToken,
        vehicle,
        null,
        DateTime.local().minus({ days: 30 }).startOf('day'),
        getEndTs(),
        5,
        null,
        type))
  }

  const isDownloadingTemperatureReport = downloading?.climate_logs?.temperature?.status === 'working' && downloading?.climate_logs?.temperature?.boxId === vehicleId
  const isDownloadingHumidityReport = downloading?.climate_logs?.humidity?.status === 'working' && downloading?.climate_logs?.humidity?.boxId === vehicleId

  return (
    <Fragment>
      {
        services.hasService(ServiceType.BatteryWarnings) && vehicle?.currentStatus?.batteries?.length ?
          <Paper title={t('vehicleDetails.battery')}>
            {
              (logs_per_battery && log_loading.fetchBatteryLogs !== 'loading') ?
                vehicle?.currentStatus?.batteries.map(battery => {
                  return <BatteryLogGraph
                    oldPicker={false}
                    setStartTime={setBatteryStartTime}
                    setEndTime={setBatteryEndTime}
                    startTime={batteryStartTime}
                    endTime={batteryEndTime}
                    key={'battery_log_graph_' + battery.id}
                    log_loading={log_loading}
                    vehicle={vehicle}
                    logs={logs_per_battery[battery.id]}
                    showBatteryId={vehicle.currentStatus?.batteries.length > 1}
                    batteryId={battery.id}

                  />
                })
                : log_loading.fetchBatteryLogs === 'loading' ? <BubbleLoadingWidget text={t('vehicleDetails.loadingBatteryData')} /> : <P>Could not fetch battery data.</P>
            }
          </Paper> : null
      }

      {services.hasService(ServiceType.WeightReports) && vehicle?.currentStatus?.weightSystem ?
        <Paper title={t('vehicleDetails.weight')}>

          <KpiRow width={'100%'} data={kpiData_weight} />

          {
            log_loading.fetchWeightLogs !== 'loading' ?
              <LogChart
                oldPicker={false}
                setStartTime={setWeightStartTime}
                setEndTime={setWeightEndTime}
                startTime={weightStartTime}
                endTime={weightEndTime}
                data={prepared_weights}
                data_key="gross"
                data_name={t('vehicleDetails.vehicleWeight')}
                max_name="Max. Vehicle Weight"
                title={t('vehicleDetails.vehicleWeight')}
                unit={weightUnit}
                line_type={'stepAfter'}
                domain={[
                  min,
                  adjustedMax
                ]}
                displayTimeInHours={getDaysDifference(weightStartTime, weightEndTime) <= 1}
        
                ticks={getTicks(null, true, weightStartTime, weightEndTime)}
              />
              : log_loading.fetchWeightLogs === 'loading' ? <BubbleLoadingWidget text={t('vehicleDetails.loadingWeightData')} /> : <P>Could not fetch weight data.</P>
          }
        </Paper> : null
      }


      {services.hasService(ServiceType.ClimateReports) && getThermometers(vehicle).length ?
        <ClimateSensorGraph
          oldPicker={false}
          setStartTime={setTemperatureStartTime}
          setEndTime={setTemperatureEndTime}
          startTime={temperatureStartTime}
          endTime={temperatureEndTime}
          vehicle={vehicle}
          vehicleId={vehicleId}
          type={'temperature'}
          title={t('vehicleDetails.temperature')}
          loadingStatus={log_loading.fetchVehicleTemperatureLogs ?? vehicle_loading.fetchVehicle }
          onDownload={_ => downloadClimateLogsWithAccessToken(climateSensorReportTypeOptions.temperature)}
          isDownloadingReport={isDownloadingTemperatureReport}
          kpiData={kpiData_temperature}
          downloadStatus={downloading?.climate_logs?.temperature}
          logs={prepared_temperature_logs}
          ticks={getTicks(null, true, temperatureStartTime, temperatureEndTime)}
          loadingMessage={t('vehicleDetails.loadingTemperatureData')}
          loadingFailedMessage={'Could not fetch temperature data.'}
        /> : null
      }

      {!isHumiditySensorBroken && services.hasService(ServiceType.ClimateReports) && getHumiditySensors(vehicle).length ?
        <ClimateSensorGraph
          oldPicker={false}
          setStartTime={setHumidtyStartTime}
          setEndTime={setHumidtyEndTime}
          startTime={humidtyStartTime}
          endTime={humidtyEndTime}
          vehicle={vehicle}
          vehicleId={vehicleId}
          type={'humidity'}
          title={t('vehicleDetails.humidity')}
          loadingStatus={log_loading.fetchVehicleHumidityLogs ?? vehicle_loading.fetchVehicle }
          onDownload={_ => downloadClimateLogsWithAccessToken(climateSensorReportTypeOptions.humidity)}
          isDownloadingReport={isDownloadingHumidityReport}
          kpiData={kpiData_humidity}
          downloadStatus={downloading?.climate_logs?.humidity}
          logs={prepared_humidity_logs}
          ticks={getTicks(null, true, humidtyStartTime, humidtyEndTime)}
          loadingMessage={t('vehicleDetails.loadingHumidityData')}
          loadingFailedMessage={'Could not fetch humidity data.'}
        /> : null
      }

      {
        services.hasService(ServiceType.BatteryWarnings) && vehicle?.currentStatus?.lithiumPowerSupply ?
          <Paper title={t('vehicleDetails.lithiumPowerSupply')}>
            {
              (log_loading.fetchLpsLogs !== 'loading') ?
                <LpsLogGraph
                  oldPicker={false}
                  setStartTime={setLpsStartTime}
                  setEndTime={setLpsEndTime}
                  startTime={lpsStartTime}
                  endTime={lpsEndTime}
                  key={'lps'}
                  log_loading={log_loading}
                  vehicle={vehicle}
                  logs={prepared_lps_logs}
                />
                : log_loading.fetchLpsLogs === 'loading' ? <BubbleLoadingWidget text={t('vehicleDetails.loadingLithiumData')} /> : <P>Could not fetch lithium power supply data.</P>
            }
          </Paper> : null
      } 

    </Fragment>
  )
}

const mapStateToProps = ({
  weight_logs,
  battery_logs,
  temperature_logs,
  humidity_logs,
  log_loading,
  vehicle_loading,
  loading,
  subtrees,
  downloading,
  lps_logs,
}) => ({
  weight_logs,
  battery_logs,
  temperature_logs,
  humidity_logs,
  log_loading,
  vehicle_loading,
  loading,
  subtrees,
  downloading,
  lps_logs
})

const mapDispatchToProps = dispatch => ({
  fetchVehicleWeights: (accessToken, startEpoch, endEpoch, id, samplingInterval) => dispatch(fetchVehicleWeights(accessToken, startEpoch, endEpoch, id, samplingInterval)),
  fetchVehicleBatteryLogs: (accessToken, startEpoch, endEpoch, id, samplingInterval) => dispatch(fetchVehicleBatteryLogs(accessToken, startEpoch, endEpoch, id, samplingInterval)),
  fetchVehicleTemperatureLogs: (accessToken, startEpoch, endEpoch, id, samplingInterval) => dispatch(fetchVehicleTemperatureLogs(accessToken, startEpoch, endEpoch, id, samplingInterval)),
  fetchVehicleHumidityLogs: (accessToken, startEpoch, endEpoch, id, samplingInterval) => dispatch(fetchVehicleHumidityLogs(accessToken, startEpoch, endEpoch, id, samplingInterval)),
  downloadClimateLogs: (accessToken, vehicle, deviceType, start, end, samplingInterval, avgOverMinutes, typeOfReport) => dispatch(downloadClimateLogs(accessToken, vehicle, deviceType, start, end, samplingInterval, avgOverMinutes, typeOfReport)),
  fetchLpsBatteryLogs: (accessToken, startEpoch, endEpoch, id, samplingInterval) => dispatch(fetchLpsBatteryLogs(accessToken, startEpoch, endEpoch, id, samplingInterval)),
})

export default connect(mapStateToProps, mapDispatchToProps)(LogGraphs)


